import cx from 'classnames'
import Styles from './PlanDetails.module.scss'

import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Text } from 'blaze-ui/Text/Text'
import { Brand, Check2, CopyRight2, Design, Gift01, Lightbulb1, TemplatesFilled, UsersProfiles1 } from 'blaze-ui/icons'
import Server02 from 'blaze-ui/icons/16/Server02'
import { Email } from 'blaze-ui/icons/24'
import { Headphones } from 'blaze-ui/icons/24/Headphones'
import { useReferralUrl } from 'components/Settings/ReferralModalV2/hooks/useReferralUrl'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Button } from 'blaze-ui/Button'
import { FacebookBrand } from 'blaze-ui/icons/35'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { useModals } from 'almanac-ui/Modal'
import { ReferralModalV2 } from 'components/Settings/ReferralModalV2/ReferralModalV2'
import { FREE_REWARD_TIERS } from 'hooks/useAutopilotLimits'
import CalendarPost from 'blaze-ui/icons/20/CalendarPost'
import { BlazeAI } from 'almanac-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import InformationCircleSmall from 'blaze-ui/icons/16/InformationCircleSmall'
import { Tooltip } from 'components/Tooltip/Tooltip'

type PlanFeatureConfig = {
  id: string
  label: string | ((generationCredits: string) => string)
  subtitle?: string
  textColor?: string
  tooltipLabel: string
}

const STARTER_PLAN_FEATURES: PlanFeatureConfig[] = [
  {
    id: 'posting-accounts',
    label: '3 posting accounts',
    subtitle: '(Additional accounts $10/month)',
    textColor: 'var(--dark-60)',
    tooltipLabel: 'Social media or other accounts you connect to Blaze so it can post on your behalf.',
  },
  {
    id: 'generation-credits',
    label: (credits) => `${credits} generation credits`,
    tooltipLabel: 'Each standard content generation uses 1 credit, with advanced features using additional credits.',
  },
  {
    id: 'planning-window',
    label: 'Weekly planning window',
    tooltipLabel: 'Ability to plan your content one week in advance.',
  },
  {
    id: 'users',
    label: '1 user',
    tooltipLabel: 'Designed for a solo founder or marketer managing content on their own.',
  },
  {
    id: 'ai-learning-loops',
    label: 'AI learning loops',
    tooltipLabel: 'Tracks your performance and uses those insights to improve future content automatically.',
  },
  {
    id: 'visual-styles',
    label: 'Visual Styles',
    tooltipLabel: 'Improves your images with AI-enhanced lighting and styling.',
  },
  {
    id: 'automated-ad-campaigns',
    label: 'Automated ad campaigns',
    tooltipLabel: 'Creates and improves your ad campaigns, optimizing performance weekly.',
  },
]

const GROWTH_PLAN_FEATURES: PlanFeatureConfig[] = [
  {
    id: 'posting-accounts',
    label: '10 posting accounts',
    subtitle: '(Additional accounts $10/month)',
    textColor: 'var(--dark-60)',
    tooltipLabel: 'Social media or other accounts you connect to Blaze so it can post on your behalf.',
  },
  {
    id: 'generation-credits',
    label: (credits) => `${credits} generation credits`,
    tooltipLabel: 'Each standard content generation uses 1 credit, with advanced features using additional credits.',
  },
  {
    id: 'planning-window',
    label: 'Yearly Planning Window',
    tooltipLabel: 'Plan out content weeks, months, or quarters ahead.',
  },
  {
    id: 'users',
    label: 'Unlimited users',
    tooltipLabel: 'Invite your entire team to collaborate, create, and publish together.',
  },
  {
    id: 'approvals',
    label: 'Approvals',
    tooltipLabel: 'Ensure nothing gets published without explicit approval.',
  },
  {
    id: 'campaign-audiences',
    label: 'Campaign audiences',
    tooltipLabel: 'Customize your campaigns for specific audiences.',
  },
]

/** Renders a single feature row with a checkmark, label, optional subtitle, and a placeholder tooltip. */
const FeatureItem = ({
  label,
  subtitle,
  tooltipLabel = 'Coming soon — more details to follow.',
  textColor = 'var(--dark-50)',
}: {
  label: React.ReactNode
  subtitle?: string
  tooltipLabel?: string
  textColor?: string
}) => (
  <div className={cx(Styles.feature, subtitle ? Styles.alignedStart : undefined)}>
    <Check2 size={16} />
    <div className={Styles.featureContent}>
      <div className={Styles.featureLabelRow}>
        <Text variant="secondary" color="var(--dark-80)">
          {label}
        </Text>
        <Tooltip label={tooltipLabel} placement="top">
          <button type="button" className={Styles.infoButton} aria-label="More info">
            <InformationCircleSmall color="var(--dark-25)" />
          </button>
        </Tooltip>
      </div>
      {subtitle && (
        <Text variant="metadata" color={textColor}>
          {subtitle}
        </Text>
      )}
    </div>
  </div>
)

export const TwitterX = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none">
      <path
        fill="#15171A"
        fillRule="evenodd"
        d="M12.14 2.57 8.67 6.62 5.9 2.57H2.06l4.48 6.55L2 14.43h1.56l3.67-4.29 2.93 4.3H14l-4.63-6.8 4.33-5.07h-1.56Zm-1.29 10.71L7.97 9.13 4.24 3.75h.96l6.62 9.53h-.97Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

// const AddOns = () => {
//   return (
//     <div className={Styles.addOnsContainer}>
//       <div className={Styles.addOnsHeader}>
//         <Text variant="label" color="var(--dark-80)">
//           Add-ons to purchase:
//         </Text>
//       </div>
//       <div className={Styles.addOns}>
//         <Button frontIcon={Plus} variant="ghost">
//           100 Add-on Credits
//         </Button>
//         <Button frontIcon={Plus} variant="ghost">
//           Add-on Channels
//         </Button>
//         <Button frontIcon={Plus} variant="ghost">
//           Enable White Label
//         </Button>
//       </div>
//     </div>
//   )
// }

export const FeatureList = ({
  plan,
  currentPlan,
  creditLimit,
}: {
  plan: PaymentPlanKey
  currentPlan: PaymentPlanKey
  creditLimit?: number
}) => {
  const { openModal } = useModals()
  const { referralUrl } = useReferralUrl()
  const encodedReferralUrl = encodeURIComponent(referralUrl)
  const twitterPrefillText = encodeURIComponent(
    `Hey, I’ve been using @blazeaiapp to save 10+ hours a week, and it’s been a game-changer. Try it for free with my link: ${referralUrl}.`,
  )
  const colorMap: any = {
    [PaymentPlanKey.Free]: '#949494',
    [PaymentPlanKey.BlazeAutopilotStarter]: '#F6A11A',
    [PaymentPlanKey.BlazeAutopilotGrowth]: 'var(--blue-50)',
    [PaymentPlanKey.BlazeAutopilotEnterprise]: '#9935F5',
  }
  const color = colorMap[plan]

  const generationCreditsMap: Partial<Record<PaymentPlanKey, string>> = {
    [PaymentPlanKey.BlazeAutopilotStarter]: '300',
    [PaymentPlanKey.BlazeAutopilotGrowth]: '700',
  }
  const generationCredits = generationCreditsMap[plan] ?? ''

  return (
    <>
      <div className={Styles.featureList}>
        {plan === PaymentPlanKey.Free && (
          <>
            <Text variant="metadata" color="var(--dark-60)">
              Includes:
            </Text>
            <div className={Styles.feature}>
              <TemplatesFilled size={16} color={color} />
              <Text variant="secondary" color="var(--dark-80)">
                Limited 3 accounts
              </Text>
            </div>
            <div className={Styles.feature}>
              <Server02 color={color} />
              <Text variant="secondary" color="var(--dark-80)">
                No credits
              </Text>
            </div>
            <div className={Styles.feature}>
              <CalendarPost size={16} color={color} />
              <Text variant="secondary" color="var(--dark-80)">
                {FREE_REWARD_TIERS[0]} post/week per account
              </Text>
            </div>
            <div className={Styles.feature}>
              <BlazeAI size={16} color={color} />
              <Text variant="secondary" color="var(--dark-80)">
                Posts watermarked
              </Text>
            </div>
          </>
        )}
        {plan === PaymentPlanKey.BlazeAutopilotStarter && (
          <>
            <Text variant="metadata" color="var(--dark-60)">
              What you get:
            </Text>
            {STARTER_PLAN_FEATURES.map((feature) => (
              <FeatureItem
                key={feature.id}
                label={
                  typeof feature.label === 'function'
                    ? feature.label(creditLimit?.toString() ?? generationCredits)
                    : feature.label
                }
                subtitle={feature.subtitle}
                textColor={feature.textColor}
                tooltipLabel={feature.tooltipLabel}
              />
            ))}
          </>
        )}
        {plan === PaymentPlanKey.BlazeAutopilotGrowth && (
          <>
            <Text variant="metadata" color="var(--dark-60)">
              Everything in Starter plus:
            </Text>
            {GROWTH_PLAN_FEATURES.map((feature) => (
              <FeatureItem
                key={feature.id}
                label={
                  typeof feature.label === 'function'
                    ? feature.label(creditLimit?.toString() ?? generationCredits)
                    : feature.label
                }
                subtitle={feature.subtitle}
                textColor={feature.textColor}
                tooltipLabel={feature.tooltipLabel}
              />
            ))}
          </>
        )}
        {plan === PaymentPlanKey.BlazeAutopilotEnterprise && (
          <>
            <Text variant="metadata" color="var(--dark-60)">
              Everything in Growth plus:
            </Text>
            <div className={cx(Styles.feature, Styles.alignedStart)}>
              <Lightbulb1 size={16} color={color} />
              <Text variant="secondary" color="var(--dark-80)">
                Dedicated <strong>AI Strategy Expert</strong>
              </Text>
            </div>
            <div className={cx(Styles.feature, Styles.alignedStart)}>
              <Headphones size={16} color={color} />
              <Text variant="secondary" color="var(--dark-80)">
                Personalized <strong>onboarding support</strong>
              </Text>
            </div>
            <div className={Styles.feature}>
              <Headphones size={16} color={color} />
              <Text variant="secondary" color="var(--dark-80)">
                <strong>Custom</strong> integrations
              </Text>
            </div>
            <div className={Styles.feature}>
              <UsersProfiles1 size={16} color={color} />
              <Text variant="secondary" color="var(--dark-80)">
                <strong>Group-based</strong> access
              </Text>
            </div>
            <div className={Styles.feature}>
              <Brand size={16} color={color} />
              <Text variant="secondary" color="var(--dark-80)">
                <strong>White-label</strong> interface
              </Text>
            </div>
            <div className={cx(Styles.feature, Styles.alignedStart)}>
              <Design size={16} color={color} />
              <Text variant="secondary" color="var(--dark-80)">
                Custom fonts, colors, and <strong>design systems</strong>
              </Text>
            </div>
          </>
        )}
      </div>
      {plan === PaymentPlanKey.Free && currentPlan === PaymentPlanKey.Free && (
        <div className={Styles.referralContainer}>
          <div className={Styles.referralHeader}>
            <Text variant="smallList" color="var(--dark-80)">
              Unlock more weekly posts — invite your friends!
            </Text>
          </div>
          <div className={Styles.referralUrlContainer}>
            <div className={Styles.textContainer}>
              <Text lineClamp={1} color="var(--dark-40)" variant="secondary">
                {referralUrl + 'blaze.ai/agh34u'}
              </Text>
            </div>
            <IconButton
              variant="tertiary"
              onPress={() => {
                navigator.clipboard.writeText(referralUrl)
                showToastMessage('Referral URL copied!')
              }}
              icon={CopyRight2}
              size="xs"
            />
          </div>
          <div className={Styles.footerContainer}>
            <Button
              frontIcon={Email}
              variant="tertiary"
              isDisabled={!referralUrl}
              onPress={() => openModal(ReferralModalV2)}
              size="sm"
            >
              Email
            </Button>
            <ButtonLink
              to={`https://www.facebook.com/sharer/sharer.php?u=${encodedReferralUrl}`}
              target="_blank"
              variant="tertiary"
              frontIcon={FacebookBrand}
              isDisabled={!referralUrl}
              size="sm"
            >
              Share on Facebook
            </ButtonLink>
            <ButtonLink
              to={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedReferralUrl}`}
              target="_blank"
              variant="tertiary"
              frontIcon={LinkedInBrand}
              isDisabled={!referralUrl}
              size="sm"
            >
              Share onLinkedIn
            </ButtonLink>
            <ButtonLink
              to={`https://twitter.com/intent/tweet?text=${twitterPrefillText}`}
              target="_blank"
              variant="tertiary"
              frontIcon={TwitterX}
              isDisabled={!referralUrl}
              size="sm"
            >
              Share on X/Twitter
            </ButtonLink>
            <Button variant="tertiary" endIcon={Gift01} size="sm" onPress={() => openModal(ReferralModalV2)}>
              Open Referrals
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
